import type { ReactNode } from "react";
import { allDocPages } from "./content";
import DocsLayout from "./layout";

const markdown = `
This guide takes you from a fresh signup to a working API call in under five minutes.

## 1. Create an account

Sign up at [cloudquote.io/signup](/signup). You'll get access to the **API Dashboard**, where every credential, deployment, and usage metric lives.

## 2. Create your first credential

From the dashboard, open **My Account → API Credentials**. The right credential depends on where your code runs:

| Where your code runs | What to create |
| :---- | :---- |
| In a user's web browser | A **whitelisted domain** |
| On your own server | A **private application key** |
| Mixed (front-end with auth) | A **public application key** + secondary auth |

If you're not sure, start with a **private key** — it's the simplest, and you can move to a different mechanism later. See [Authentication & API Keys](/docs/authentication) for the full breakdown.

## 3. Make your first call

Once you have a private key, fetch a stock quote:

\`\`\`bash
curl -H 'Authorization: privateKey YOUR_KEY_HERE' \\
  'https://api.cloudquote.io/fcon/getQuote.json?symbol=AAPL'
\`\`\`

You should get back a JSON object with current price, change, volume, and timestamps.

## 4. Try a news search

Use the same key to query the NewsRPM news index for the latest 30 articles mentioning Apple:

\`\`\`bash
curl -X POST \\
  -H 'Authorization: privateKey YOUR_KEY_HERE' \\
  -H 'Content-Type: application/json' \\
  --data '{
    "key": "PrefixedTicker",
    "value": ["NQ:AAPL"],
    "count": 30,
    "order": "dateWithQuality"
  }' \\
  https://api.newsrpm.com/search/indexedData
\`\`\`

The response contains an array of \`Article\` objects, newest first.

## 5. Try the chat endpoint

Skip writing API plumbing and let an LLM orchestrate calls for you:

\`\`\`bash
curl -X POST \\
  -H 'Authorization: privateKey YOUR_KEY_HERE' \\
  -H 'Content-Type: application/json' \\
  --data '{
    "model": "cloudquote-chat-1",
    "messages": [
      {"role": "user", "content": "Show me Apple\\'s price and the top 3 headlines from the last hour."}
    ]
  }' \\
  https://api.cloudquote.io/chat
\`\`\`

The model will call \`getQuote\` and \`getNewsByTicker\` under the hood and return a single, synthesized answer.

## Where to go next

- **Front-end app?** Read [Front-End Integration](/docs/frontend-integration) for the CORS rules.
- **Server-side app?** Read [Back-End Integration](/docs/backend-integration).
- **Want to ingest news into NewsRPM?** Read [Publishing News via RSS](/docs/rss-feeds).
- **Building an AI agent?** Read [Building AI Agents](/docs/agents).
`;

export default function QuickstartPage(): ReactNode {
  const page = allDocPages.find(p => p.slug === "quickstart")!;
  return <DocsLayout page={page} markdown={markdown}/>;
}
